<template>
  <div class="tf" :class="{ 'tf--mobile': isMobile }">
    <header class="tf__head">
      <button type="button" class="tf__icon" :title="$t('close')" @click="back">
        <v-icon>{{ isMobile ? ($vuetify.rtl ? 'mdi-arrow-right' : 'mdi-arrow-left') : 'mdi-close' }}</v-icon>
      </button>
      <div class="tf__head-title">
        <span class="tf__kicker">{{ isEdit ? $t('taskFormEdit') : copy ? $t('taskFormCopy') : $t('taskFormNew') }}</span>
        <span v-if="status" class="tf__status-pill" :style="{ '--sc': status.color || '#90a4ae' }">
          <i />{{ status.name }}
        </span>
      </div>
      <v-spacer />
      <v-btn
        v-if="isMobile"
        color="primary"
        depressed
        height="36"
        :loading="isLoading"
        @click="isEdit ? update() : insert()"
      >
        {{ isEdit ? $t('update') : $t('submit') }}
      </v-btn>
    </header>

    <v-form ref="form" v-model="valid" lazy-validation class="tf__form">
      <div class="tf__body">
        <!-- main column -->
        <section class="tf__main">
          <textarea
            v-model="title"
            rows="1"
            class="tf__title"
            maxlength="250"
            :placeholder="$t('please enter the title') + ' ...'"
            @input="autoGrow"
            @focus="autoGrow"
          />
          <div v-if="title && title.length > 200" class="tf__hint">{{ title.length }}/250</div>

          <div class="tf__block">
            <div class="tf__label"><v-icon small>mdi-text-box-outline</v-icon>{{ $t('taskFormDescription') }}</div>
            <tiptap-vuetify
              v-model="content"
              :extensions="extensions"
              :toolbar-attributes="{ color: $vuetify.theme.isDark ? '#2a2a2a' : '#f3f5f9', flat: true }"
              :card-props="{ flat: true, outlined: true }"
              class="tf__editor"
            />
          </div>

          <div v-if="id" class="tf__block">
            <div class="tf__label">
              <v-icon small>mdi-comment-text-multiple-outline</v-icon>{{ $t('taskFormActivity') }}
              <span v-if="commentCount" class="tf__count">{{ commentCount }}</span>
            </div>
            <task-comments :ref_id="id" @count="setCommentCount" />
          </div>
          <div class="tf__block">
            <div class="tf__label"><v-icon small>mdi-paperclip</v-icon>{{ $t('uploadFile') }}</div>
            <file-picker
              ref="refFilePicker"
              v-model="imagePicker"
              :label="$t('uploadFile')"
              :multiple="true"
              type-prop="taskAttachment"
              :val="imagePickerVal ? imagePickerVal : null"
            />
          </div>

        </section>

        <!-- properties -->
        <aside class="tf__side">
          <div
            class="tf__group"
            :class="cardDragClass('where')"
            :style="{ order: cardIndex('where') }"
            :draggable="ui.dragArmed === 'where'"
            @dragstart="onCardDragStart($event, 'where')"
            @dragover="onCardDragOver($event, 'where')"
            @drop.prevent="onCardDrop('where')"
            @dragend="onCardDragEnd"
          >
            <div class="tf__group-title">
              <span>{{ $t('taskFormWhere') }}</span>
              <span class="tf__move">
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveUp')" :aria-label="$t('taskFormMoveUp')" :disabled="cardIndex('where') === 0" @click="moveCard('where', -1)"><v-icon x-small>mdi-chevron-up</v-icon></button>
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveDown')" :aria-label="$t('taskFormMoveDown')" :disabled="cardIndex('where') === cardOrder.length - 1" @click="moveCard('where', 1)"><v-icon x-small>mdi-chevron-down</v-icon></button>
                <span class="tf__move-btn tf__drag" :data-tip="$t('taskFormDrag')" :aria-label="$t('taskFormDrag')" @mousedown="armDrag('where')" @touchstart.passive="armDrag('where')"><v-icon x-small>mdi-drag</v-icon></span>
              </span>
            </div>
            <desk-picker v-model="template" :title="$t('spaceWork')" type="statusTemplate" :set-init="true" />
            <status
              ref="refStatus"
              v-model="status"
              class="tf__status"
              type="task"
              :config="true"
              :hideconfig="true"
              :template-prop="template"
              :value="status"
            />
          </div>

          <div
            class="tf__group"
            :class="cardDragClass('schedule')"
            :style="{ order: cardIndex('schedule') }"
            :draggable="ui.dragArmed === 'schedule'"
            @dragstart="onCardDragStart($event, 'schedule')"
            @dragover="onCardDragOver($event, 'schedule')"
            @drop.prevent="onCardDrop('schedule')"
            @dragend="onCardDragEnd"
          >
            <div class="tf__group-title">
              <span>{{ $t('taskFormSchedule') }}</span>
              <span class="tf__move">
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveUp')" :aria-label="$t('taskFormMoveUp')" :disabled="cardIndex('schedule') === 0" @click="moveCard('schedule', -1)"><v-icon x-small>mdi-chevron-up</v-icon></button>
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveDown')" :aria-label="$t('taskFormMoveDown')" :disabled="cardIndex('schedule') === cardOrder.length - 1" @click="moveCard('schedule', 1)"><v-icon x-small>mdi-chevron-down</v-icon></button>
                <span class="tf__move-btn tf__drag" :data-tip="$t('taskFormDrag')" :aria-label="$t('taskFormDrag')" @mousedown="armDrag('schedule')" @touchstart.passive="armDrag('schedule')"><v-icon x-small>mdi-drag</v-icon></span>
              </span>
            </div>
            <div class="tf__quick">
              <button
                v-for="(day, index) in days"
                :key="index"
                type="button"
                class="tf__quick-day"
                :class="{ on: dueDate && day.date && isQuick(day.day) }"
                @click="setDate(day.day)"
              >
                <strong>{{ $t(day.text) }}</strong>
                <span>{{ day.date }}</span>
              </button>
            </div>
            <div class="tf__pair">
              <div class="tf__date">
                <v-text-field
                  id="tf-startDate"
                  v-model="startDate"
                  :label="$t('start-date')"
                  prepend-inner-icon="mdi-calendar-start"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="startDate"
                  element="tf-startDate"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  clearable
                  :max="dueDate || undefined"
                  auto-submit
                  :color="primaryColor"
                />
              </div>
              <div class="tf__time">
                <v-text-field
                  id="tf-startTime"
                  v-model="startTime"
                  :label="$t('start-time')"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="startTime"
                  element="tf-startTime"
                  :locale="$store.state.date_locale"
                  type="time"
                  :color="primaryColor"
                />
              </div>
            </div>
            <div class="tf__pair">
              <div class="tf__date">
                <v-text-field
                  id="tf-dueDate"
                  v-model="dueDate"
                  :label="$t('due-date')"
                  prepend-inner-icon="mdi-calendar-end"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="dueDate"
                  element="tf-dueDate"
                  clearable
                  :min="startDate || undefined"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  auto-submit
                  :color="primaryColor"
                />
              </div>
              <div class="tf__time">
                <v-text-field
                  id="tf-dueTime"
                  v-model="dueTime"
                  :label="$t('due-time')"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="dueTime"
                  element="tf-dueTime"
                  type="time"
                  :locale="$store.state.date_locale"
                  :color="primaryColor"
                />
              </div>
            </div>
          </div>

          <div
            class="tf__group"
            :class="cardDragClass('people')"
            :style="{ order: cardIndex('people') }"
            :draggable="ui.dragArmed === 'people'"
            @dragstart="onCardDragStart($event, 'people')"
            @dragover="onCardDragOver($event, 'people')"
            @drop.prevent="onCardDrop('people')"
            @dragend="onCardDragEnd"
          >
            <div class="tf__group-title">
              <span>{{ $t('taskFormPeople') }}</span>
              <span class="tf__move">
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveUp')" :aria-label="$t('taskFormMoveUp')" :disabled="cardIndex('people') === 0" @click="moveCard('people', -1)"><v-icon x-small>mdi-chevron-up</v-icon></button>
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveDown')" :aria-label="$t('taskFormMoveDown')" :disabled="cardIndex('people') === cardOrder.length - 1" @click="moveCard('people', 1)"><v-icon x-small>mdi-chevron-down</v-icon></button>
                <span class="tf__move-btn tf__drag" :data-tip="$t('taskFormDrag')" :aria-label="$t('taskFormDrag')" @mousedown="armDrag('people')" @touchstart.passive="armDrag('people')"><v-icon x-small>mdi-drag</v-icon></span>
              </span>
            </div>
            <desk-picker v-model="functor" title="functor" type="partners" />
            <desk-picker v-model="participants" title="participants" type="partners" :multiple="true" />
            <tag v-model="tag" :solo="false" :input="tag" />
          </div>

          <div
            class="tf__group"
            :class="cardDragClass('relation')"
            :style="{ order: cardIndex('relation') }"
            :draggable="ui.dragArmed === 'relation'"
            @dragstart="onCardDragStart($event, 'relation')"
            @dragover="onCardDragOver($event, 'relation')"
            @drop.prevent="onCardDrop('relation')"
            @dragend="onCardDragEnd"
          >
            <div class="tf__group-title">
              <span>{{ $t('taskFormRelation') }}</span>
              <span class="tf__move">
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveUp')" :aria-label="$t('taskFormMoveUp')" :disabled="cardIndex('relation') === 0" @click="moveCard('relation', -1)"><v-icon x-small>mdi-chevron-up</v-icon></button>
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveDown')" :aria-label="$t('taskFormMoveDown')" :disabled="cardIndex('relation') === cardOrder.length - 1" @click="moveCard('relation', 1)"><v-icon x-small>mdi-chevron-down</v-icon></button>
                <span class="tf__move-btn tf__drag" :data-tip="$t('taskFormDrag')" :aria-label="$t('taskFormDrag')" @mousedown="armDrag('relation')" @touchstart.passive="armDrag('relation')"><v-icon x-small>mdi-drag</v-icon></span>
              </span>
            </div>
            <div class="tf__seg">
              <button
                v-for="t in typeItems || []"
                :key="t.value"
                type="button"
                :class="{ on: typeMethod === t.value }"
                @click="typeMethod = typeMethod === t.value ? null : t.value"
              >
                {{ t.text }}
              </button>
            </div>
            <desk-picker
              v-if="typeMethod"
              :key="'rel-' + typeMethod"
              ref="refRelation"
              v-model="relation"
              :title="typeMethod == 'account' ? 'customer' : 'invoice'"
              :type="typeMethod == 'account' ? 'person' : 'invoices'"
              :new-btn="typeMethod == 'account'"
            />
            <div v-if="typeMethod == 'account' && relation" class="tf__facts">
              <span v-if="relation.mobile"><v-icon x-small>mdi-cellphone</v-icon>{{ relation.mobile }}</span>
              <span v-if="relation.phone"><v-icon x-small>mdi-phone-outline</v-icon>{{ relation.phone }}</span>
              <span v-if="relation.meta && relation.meta.branch"><v-icon x-small>mdi-source-branch</v-icon>{{ relation.meta.branch.name }}</span>
              <span v-if="relation.meta && relation.meta.referral_code"><v-icon x-small>mdi-ticket-confirmation-outline</v-icon>{{ relation.meta.referral_code }}</span>
            </div>
            <v-text-field
              v-model="link"
              :label="$t('link')"
              prepend-inner-icon="mdi-link-variant"
              outlined
              clearable
              dense
              hide-details
            />
          </div>

          <div
            class="tf__group tf__reminders"
            :class="cardDragClass('reminders')"
            :style="{ order: cardIndex('reminders') }"
            :draggable="ui.dragArmed === 'reminders'"
            @dragstart="onCardDragStart($event, 'reminders')"
            @dragover="onCardDragOver($event, 'reminders')"
            @drop.prevent="onCardDrop('reminders')"
            @dragend="onCardDragEnd"
          >
            <div class="tf__group-title">
              <span>{{ $t('taskFormReminders') }}</span>
              <span class="tf__move">
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveUp')" :aria-label="$t('taskFormMoveUp')" :disabled="cardIndex('reminders') === 0" @click="moveCard('reminders', -1)"><v-icon x-small>mdi-chevron-up</v-icon></button>
                <button type="button" class="tf__move-btn" :data-tip="$t('taskFormMoveDown')" :aria-label="$t('taskFormMoveDown')" :disabled="cardIndex('reminders') === cardOrder.length - 1" @click="moveCard('reminders', 1)"><v-icon x-small>mdi-chevron-down</v-icon></button>
                <span class="tf__move-btn tf__drag" :data-tip="$t('taskFormDrag')" :aria-label="$t('taskFormDrag')" @mousedown="armDrag('reminders')" @touchstart.passive="armDrag('reminders')"><v-icon x-small>mdi-drag</v-icon></span>
              </span>
            </div>
            <notification v-model="notifications" :value="notifications" />
          </div>
        </aside>
      </div>

      <footer v-if="!isMobile" class="tf__foot">
        <v-btn v-if="!id" color="primary" depressed height="38" :loading="isLoading" @click="insert">
          <v-icon left small>mdi-check</v-icon>{{ $t('submit') }}
        </v-btn>
        <v-btn v-else color="primary" depressed height="38" :loading="isLoading" @click="update">
          <v-icon left small>mdi-check</v-icon>{{ $t('update') }}
        </v-btn>
        <span class="tf__foot-hint">{{ $t('taskFormRequiredHint') }}</span>
        <v-spacer />
        <v-btn text height="38" color="grey darken-1" @click="back">{{ $t('cancel') }}</v-btn>
      </footer>
    </v-form>

    <snakbar
      v-model="snakbar"
      :text="snakbarText"
      :active="snakbar"
      :color="snakbarColor ? snakbarColor : 'primary'"
    />
  </div>
</template>

<script>
// Copy of pages/task/task.vue for the task desk (the original stays
// untouched). Script is unchanged except imports, the account pickers
// (SettingAccountPicker) and the relation-type watcher; payload is still
// `this.$data`, so do not add UI-only state to data().
import Status from "../../../components/general/Status/status";
import Vue from "vue";
import TaskComments from "./TaskComments";
import DeskPicker from "../../setting/components/SettingAccountPicker";
import General from "@/Mixins/general";
import filePicker from "../../../components/general/filePicker/index";
import {
  // component
  TiptapVuetify,
  // extensions
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
  Image,
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "tiptap-vuetify";
// Side cards the user can reorder (top to bottom = priority).
const TASK_CARDS = ["where", "schedule", "people", "relation", "reminders"];
const CARD_ORDER_KEY = "taskForm.sideCardOrder";

export default {
  name: "TaskForm",
  components: {
    TiptapVuetify,
    filePicker,
    DeskPicker,
    TaskComments,
    Snakbar: () => import("../../../components/general/snakbar/snakbar"),
    Tag: () => import("../../../components/general/Tag/tag"),
    Status,
  },
  mixins: [General],
  props: [
    "refId",
    "taskType",
    "taskId",
    "refItem",
    "refType",
    "copy",
    "idProp",
    "dialogStatus",
    "currentStatus",
    "currentBoard",
  ],
  data: () => ({
    extensions: [
      History,
      Table,
      TableCell,
      TableHeader,
      TableRow,
      Blockquote,
      Link,
      Underline,
      Strike,
      Italic,
      ListItem, // if you need to use a list (BulletList, OrderedList)
      BulletList,
      OrderedList,
      Image,
      [
        Heading,
        {
          // Options that fall into the tiptap's extension
          options: {
            levels: [1, 2, 3],
          },
        },
      ],
      Bold,
      Code,
      HorizontalRule,
      Paragraph,
      HardBreak, // line break on Shift + Ctrl + Enter
    ],
    content: "",
    snakbar: false,
    notifications: null,
    functor: null,
    taskAttachment: null,
    imagePicker: null,
    imagePickerVal: null,
    template: null,
    relation: null,
    // typeMethod: 'account',
    typeMethod: null,
    snakbarText: null,
    participants: null,
    snakbarColor: null,
    isLoading: false,
    timeDialog: false,
    gender: "man",
    startDate: null,
    startTime: null,
    dueDate: null,
    dueDateWatch: null,
    dueTime: null,
    typeItems: null,
    tag: null,
    link: null,
    description: null,
    id: null,
    status: null,
    new: false,
    valid: null,
    title: null,
    ref_id: null,
    type: null,
    days: [],
  }),
  computed: {
    // isMobile comes from the global mixin (width < 800).
    isEdit() {
      return !!this.id;
    },
    commentCount() {
      return this.ui ? this.ui.commentCount : 0;
    },
    cardOrder() {
      return this.ui ? this.ui.cardOrder : TASK_CARDS;
    },
    primaryColor() {
      return this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? "dark" : "light"].primary;
    },
  },
  watch: {
    currentBoard: {
      handler(val) {
        if (val) {
          this.template = val;
        }
      },
      deep: true,
      immediate: true,
    },
    currentStatus: {
      handler(val) {
        if (val) {
          this.status = val;
        }
      },
      deep: true,
      immediate: true,
    },
    dialogStatus: {
      handler(val) {
        if (!val) {
          this.clearForm();
        }
      },
      deep: true,
    },
    idProp: {
      handler(val) {
        if (val) {
          this.getItem();
        }
      },
      deep: true,
      immediate: true,
    },
    template: {
      handler(val) {
        if (val) {
          localStorage.setItem("taskTemplate", JSON.stringify(val));
          if (val && this.$refs.refStatus) {
            this.$nextTick(() => {
              this.$refs.refStatus.getItems(val.id);
            });
          }
        }
      },
      deep: true,
      immediate: true,
    },
    typeMethod() {
      // The relation picker is keyed by typeMethod, so it remounts with
      // the new type; only the stale selection needs clearing.
      if (!this.loading) {
        this.relation = null;
      }
    },
    refItem() {
      this.typeMethod = this.refType;
      this.relation = this.refItem;
    },
    timeDialog() {
      if (this.timeDialog === false) {
        // this.create()
      }
    },
    taskId(val, oldVal) {
      this.clearForm();
      if (val) {
        this.getItem();
      }
    },
    // tag () {
    //   this.create()
    // },
    // status () {
    //   this.create()
    // },
    // description () {
    //   this.create()
    // },
  },
  created() {
    // UI-only state lives outside data() (data() is posted as the payload).
    this.ui = Vue.observable({
      commentCount: 0,
      cardOrder: TASK_CARDS.slice(),
      dragArmed: null,
      dragging: null,
      dragOver: null,
    });
    this.loadCardOrder();
    // tiptap-vuetify takes its tooltip language from $vuetify.lang.current,
    // which this app leaves at "en"; use the app locale while the form is open.
    this.prevVuetifyLang = this.$vuetify.lang.current;
    this.$vuetify.lang.current = this.$i18n.locale;
  },
  beforeDestroy() {
    if (this.prevVuetifyLang) this.$vuetify.lang.current = this.prevVuetifyLang;
  },
  beforeMount() {
    this.typeItems = [
      {
        value: "invoice",
        text: this.$t("invoice"),
      },
      {
        value: "account",
        text: this.$t("customer"),
      },
    ];
    var template = localStorage.getItem("taskTemplate");
    if (template && template !== undefined) {
      this.template = JSON.parse(template);
    }
  },
  mounted() {
    var m1 = this.$moment();
    var m2 = this.$moment();
    var m3 = this.$moment();
    var m4 = this.$moment();
    var m5 = this.$moment();
    var format = "D MMM";
    var locale = "en";
    if (this.$store.state.date_locale === "fa,en") {
      format = "jD jMMM";
      locale = "fa";
    }
    this.days = [
      {
        day: 0,
        date: m1.add(0, "day").locale(locale).format(format),
        color: "pink",
        text: "today",
      },
      {
        day: 1,
        date: m2.add(1, "day").locale(locale).format(format),
        color: "orange",
        text: "tomorrow",
      },
      {
        day: 7,
        date: m3.add(7, "day").locale(locale).format(format),
        color: "blue-grey",
        text: "next-week",
      },
      {
        day: 14,
        date: m4.add(14, "day").locale(locale).format(format),
        color: "cyan",
        text: "two-week",
      },
      {
        day: 28,
        date: m5.add(28, "day").locale(locale).format(format),
        color: "green",
        text: "four-week",
      },
    ];
    this.currency = JSON.parse(localStorage.getItem("token")).init.currency;
    if (this.taskId) {
      this.typeMethod = null;
      this.getItem();
    }
    if (this.$route.params.id != null) {
      // this.taskId = this.$route.params.id
      this.id = this.$route.params.id;
      this.typeMethod = null;
      this.getItem();
    }
    if (this.refItem) {
      this.typeMethod = this.refType;
      this.relation = this.refItem;
    }
  },
  methods: {
    autoGrow(e) {
      const el = e.target;
      el.style.height = "auto"; // reset
      el.style.height = el.scrollHeight + "px"; // fit content
    },
    back() {
      this.$emit("close");
      // this.goTo('taskDesk')
    },
    setDate(day) {
      var m = this.$moment();
      m.add(day, "day");
      var date = null;
      if (this.$store.state.date_locale === "fa,en") {
        date = m.format("jYYYY/jMM/jDD");
      }
      if (this.$store.state.date_locale === "en,fa") {
        date = m.format("YYYY/MM/DD");
      }
      this.dueDate = date;
      this.startDate = null;
      // this.create()
    },
    setCommentCount(n) {
      this.ui.commentCount = n;
    },
    cardIndex(key) {
      return this.cardOrder.indexOf(key);
    },
    // Side cards order is per user, stored with the panel-ui API
    // (docs/api/panel-ui-settings.md) as a comma separated list.
    loadCardOrder() {
      this.$axios
        .get(this.$endpoint + "panel-ui", { params: { key: CARD_ORDER_KEY } })
        .then((res) => {
          var data = res.data && res.data.data;
          if (!data || !data.value) return;
          var saved = String(data.value).split(",").filter((k) => TASK_CARDS.includes(k));
          // Cards added after the user saved go to the end.
          TASK_CARDS.forEach((k) => {
            if (!saved.includes(k)) saved.push(k);
          });
          this.ui.cardOrder = saved;
        })
        .catch(() => {});
    },
    moveCard(key, step) {
      var from = this.ui.cardOrder.indexOf(key);
      this.placeCard(key, from + step);
    },
    placeCard(key, to) {
      var order = this.ui.cardOrder.slice();
      var from = order.indexOf(key);
      if (from < 0 || to < 0 || to >= order.length || from === to) return;
      order.splice(to, 0, order.splice(from, 1)[0]);
      this.ui.cardOrder = order;
      this.saveCardOrder();
    },
    saveCardOrder() {
      var order = this.ui.cardOrder;
      this.$axios
        .post(this.$endpoint + "panel-ui", { key: CARD_ORDER_KEY, value: order.join(",") })
        .catch(() => {});
    },
    // Drag only from the handle: the card is draggable while it is pressed,
    // so text selection in the card's inputs keeps working.
    armDrag(key) {
      this.ui.dragArmed = key;
      var off = () => {
        if (!this.ui.dragging) this.ui.dragArmed = null;
        window.removeEventListener("mouseup", off);
        window.removeEventListener("touchend", off);
      };
      window.addEventListener("mouseup", off);
      window.addEventListener("touchend", off);
    },
    onCardDragStart(e, key) {
      if (this.ui.dragArmed !== key) {
        e.preventDefault();
        return;
      }
      this.ui.dragging = key;
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", key);
    },
    onCardDragOver(e, key) {
      if (!this.ui.dragging) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      this.ui.dragOver = key === this.ui.dragging ? null : key;
    },
    onCardDrop(key) {
      var dragged = this.ui.dragging;
      if (dragged && dragged !== key) this.placeCard(dragged, this.cardIndex(key));
      this.onCardDragEnd();
    },
    onCardDragEnd() {
      this.ui.dragging = null;
      this.ui.dragOver = null;
      this.ui.dragArmed = null;
    },
    cardDragClass(key) {
      var over = this.ui.dragOver === key;
      var from = this.cardIndex(this.ui.dragging);
      return {
        "tf__group--dragging": this.ui.dragging === key,
        // line on the side the dragged card will land
        "tf__group--drop-before": over && from > this.cardIndex(key),
        "tf__group--drop-after": over && from < this.cardIndex(key),
      };
    },
    // Whether dueDate equals the quick-pick "today + day" (same format as setDate).
    isQuick(day) {
      var m = this.$moment().add(day, "day");
      var fa = this.$store.state.date_locale === "fa,en";
      return this.dueDate === m.format(fa ? "jYYYY/jMM/jDD" : "YYYY/MM/DD") && !this.startDate;
    },
    insert() {
      this.new = true;
      this.create();
    },
    create() {
      if (!this.id && !this.new) return false;

      if (!this.title) {
        this.snakbar = true;
        this.snakbarText = this.$t("please enter the title");
        this.snakbarColor = "red";
        return false;
      }

      var app = this;
      app.type = app.taskType;
      app.ref_id = app.refId;
      app.isLoading = true;
      app.description = app.content;
      app.$axios
        .post(app.$endpoint + "task", app.$data)
        .then((res) => {
          app.id = res.data.data.id;
          if (app.imagePicker && app.imagePicker.length > 0) {
            app.$refs.refFilePicker.create(app.id);
          }
          if (app.new) {
            app.snakbarText = app.$t("alert-created");
          } else {
            this.snakbarText = app.$t("alert-edited");
          }
          app.snakbar = true;
          app.clearForm();
          app.back();
          app.$refs.form.resetValidation();
          if (app.copy === true) {
            // app.$router.go(-1)
            app.$emit("close");
          }
          app.$emit("fetchBoard");
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => {
          app.new = false;
          app.isLoading = false;
        });
    },
    update() {
      if (!this.title) {
        return false;
      }
      if (!this.id && !this.new) return false;
      var app = this;
      app.type = app.taskType;
      app.ref_id = app.refId;
      app.isLoading = true;
      app.description = app.content;
      app.$axios
        .post(app.$endpoint + "task", app.$data)
        .then((res) => {
          app.id = res.data.data.id;
          if (app.imagePicker && app.imagePicker.length > 0) {
            app.$refs.refFilePicker.create(app.id);
          }
          if (app.new) {
            app.snakbarText = app.$t("alert-created");
          } else {
            app.snakbarText = app.$t("alert-edited");
          }
          app.snakbar = true;
          // app.$router.go(-1)
          app.$emit("close");
          app.$emit("fetchBoard", {
            mode: "update",
            id: app.id,
            status: app.status,
            title: app.title,
            tag: app.tag,
            functor: app.functor,
            participants: app.participants,
            dueDate: app.dueDate,
            link: app.link,
            relation: app.relation,
          });
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => {
          app.new = false;
          app.isLoading = false;
        });
    },
    getItem() {
      // this.clearForm()
      var app = this;
      app.loading = true;
      let idRouter = app.$route.params.id;
      let availableRoute = idRouter ? idRouter : app.idProp;
      app.$axios
        .get(this.$endpoint + "task/" + availableRoute + "/edit", {
          params: {
            id: availableRoute,
          },
        })
        .then((res) => {
          res = res.data;
          if (app.copy) {
            app.id = null;
          } else {
            app.id = res.id;
          }
          app.title = res.title;
          app.typeMethod = res.type;
          app.imagePicker = res.task_attachment;
          app.imagePickerVal = res.task_attachment;
          app.notifications = res.notifications;
          app.functor = res.functor;
          // console.log(app.functor, "app.functor ");
          app.relation = res.relation;
          app.content = res.content;
          app.startDate = res.due_date;
          app.participants = res.party;
          app.link = res.link;
          app.startTime = res.due_time;
          app.template = res.last_status.status.template;
          app.status = res.last_status.status;
          app.dueDate = res.due_end_date;
          app.dueTime = res.due_end_time;
          // if (res.tags.length > 0) {
          app.tag = res.tags;
          // }
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    clearForm() {
      this.content = "";
      this.snakbar = false;
      this.snakbarText = null;
      this.snakbarColor = null;
      this.isLoading = false;
      this.timeDialog = false;
      // this.typeMethod = 'account'
      this.typeMethod = null;
      this.relation = null;
      this.taskAttachment = null;
      this.startDate = null;
      this.startTime = null;
      this.dueDate = null;
      this.dueDateWatch = null;
      this.dueTime = null;
      this.tag = null;
      this.link = null;
      this.description = null;
      this.id = null;
      this.new = false;
      this.participants = null;
      this.title = null;
      this.ref_id = null;
      this.type = null;
      this.functor = null;

      this.$emit("idNull");
    },
  },
};
</script>

<style scoped>
.tf {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fff;
}
.theme--dark .tf {
  background: #1e1e1e;
}
/* A global rule pins .v-input to 20px; restore Vuetify's default here. */
.tf ::v-deep .v-input {
  height: auto !important;
}
.tf__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid #eef0f4;
}
.theme--dark .tf__head {
  border-color: #333;
}
.tf__icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tf__icon:hover {
  background: rgba(0, 0, 0, 0.06);
}
.tf__head-title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.tf__kicker {
  font-weight: 700;
  font-size: 0.95rem;
}
.tf__status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  color: var(--sc);
  border: 1px solid var(--sc);
  white-space: nowrap;
}
.tf__status-pill i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--sc);
}
.tf__form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.tf__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  overflow: hidden;
}
.tf__main {
  flex: 1 1 auto;
  min-width: 0;
  overflow-y: auto;
  padding: 20px 24px 24px;
}
.tf__side {
  flex: 0 0 360px;
  width: 360px;
  overflow-y: auto;
  padding: 16px;
  background: #f6f7fa;
  border-inline-start: 1px solid #eef0f4;
}
.theme--dark .tf__side {
  background: #191919;
  border-color: #333;
}
/* Title stays pinned at the top while the details scroll under it. */
.tf__title {
  position: sticky;
  top: -20px;
  z-index: 3;
  display: block;
  margin: -20px -24px 0;
  width: calc(100% + 48px);
  padding: 16px 24px 10px !important;
  background: #fff;
  border-bottom: 1px solid #eef0f4 !important;
  resize: none;
  outline: none;
  overflow: hidden;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.6;
  padding: 4px 0;
  border-bottom: 2px solid transparent;
  color: inherit;
  transition: border-color 0.15s;
}
.tf__title:focus {
  border-bottom-color: var(--v-primary-base) !important;
}
.theme--dark .tf__title {
  background: #1e1e1e;
  border-bottom-color: #333 !important;
}
.tf--mobile .tf__title {
  top: 0;
  margin: -16px -16px 0;
  width: calc(100% + 32px);
  padding: 12px 16px 8px !important;
}
.tf__title::placeholder {
  color: #b3bac6;
  font-weight: 500;
}
.tf__hint {
  font-size: 0.72rem;
  color: #8a94a6;
  text-align: end;
}
.tf__block {
  margin-top: 22px;
}
.tf__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #4b5563;
  margin-bottom: 8px;
}
.theme--dark .tf__label {
  color: #ccc;
}
.tf__label .v-icon {
  color: #8a94a6;
}
.tf__editor ::v-deep .tiptap-vuetify-editor__content {
  min-height: 140px;
  max-height: 360px;
  overflow-y: auto;
}
.tf__editor ::v-deep .v-card {
  border-radius: 8px !important;
  overflow: hidden;
}
/* overrides.sass paints button content white; keep toolbar readable */
.tf__editor ::v-deep .v-toolbar .v-btn,
.tf__editor ::v-deep .v-toolbar .v-btn .v-btn__content,
.tf__editor ::v-deep .v-toolbar .v-btn .v-icon {
  color: #4b5563 !important;
  caret-color: #4b5563 !important;
}
.theme--dark .tf__editor ::v-deep .v-toolbar .v-btn,
.theme--dark .tf__editor ::v-deep .v-toolbar .v-btn .v-btn__content,
.theme--dark .tf__editor ::v-deep .v-toolbar .v-btn .v-icon {
  color: #ddd !important;
}
.tf__count {
  font-size: 0.7rem;
  font-weight: 600;
  min-width: 20px;
  height: 18px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  text-align: center;
  background: var(--v-primary-base);
  color: #fff;
}
.tf__group {
  background: #fff;
  border: 1px solid #e8ebf0;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.theme--dark .tf__group {
  background: #1e1e1e;
  border-color: #333;
}
.tf__side {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.tf__group {
  flex: 0 0 auto;
}
.tf__group-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.74rem;
  font-weight: 700;
  color: #8a94a6;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.tf__move {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s;
}
.tf__group:hover .tf__move,
.tf__move:focus-within,
.tf__group--dragging .tf__move {
  opacity: 1;
}
@media (hover: none) {
  .tf__move {
    opacity: 1;
  }
}
.tf__move-btn {
  position: relative;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: none;
  letter-spacing: 0;
}
.tf__move-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}
.theme--dark .tf__move-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}
.tf__move-btn:disabled {
  cursor: default;
}
.tf__move-btn:disabled .v-icon {
  opacity: 0.3;
}
.tf__drag {
  cursor: grab;
}
.tf__drag:active {
  cursor: grabbing;
}
/* Local tooltip: opens below the button and grows toward the card's inside
   edge, so the side panel never clips it. */
.tf__move-btn::after {
  content: attr(data-tip);
  position: absolute;
  top: calc(100% + 6px);
  inset-inline-end: 0;
  z-index: 5;
  padding: 4px 8px;
  border-radius: 6px;
  background: #1f2937;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18);
  opacity: 0;
  transform: translateY(-2px);
  transition: opacity 0.12s, transform 0.12s;
  pointer-events: none;
}
.theme--dark .tf__move-btn::after {
  background: #e5e7eb;
  color: #111827;
}
.tf__move-btn:hover:not(:disabled)::after,
.tf__move-btn:focus-visible::after {
  opacity: 1;
  transform: none;
  transition-delay: 0.25s;
}
.tf__group--dragging .tf__move-btn::after {
  display: none;
}
.tf__group {
  position: relative;
  transition: opacity 0.15s;
}
.tf__group--dragging {
  opacity: 0.45;
}
.tf__group--drop-before::before,
.tf__group--drop-after::before {
  content: "";
  position: absolute;
  inset-inline: 6px;
  height: 3px;
  border-radius: 6px;
  background: var(--v-primary-base);
}
.tf__group--drop-before::before {
  top: -8px;
}
.tf__group--drop-after::before {
  bottom: -8px;
}
.tf__status ::v-deep .v-text-field__details,
.tf__group ::v-deep .v-text-field__details {
  display: none;
}
.tf__group ::v-deep .v-input__slot {
  margin-bottom: 0 !important;
}
/* The global .app-form-notify is flex: 1 1 0 + overflow hidden (made for
   fixed-height cards); in this auto-height card it collapsed to 0px. */
.tf__reminders ::v-deep .app-form-notify {
  flex: 0 0 auto;
  min-height: auto;
  overflow: visible;
}
.tf__reminders ::v-deep .app-form-notify > .v-divider {
  display: none;
}
.tf__reminders ::v-deep .app-form-notify__add {
  justify-content: flex-start;
  background: transparent;
}
.tf__reminders ::v-deep .app-form-notify__add .v-btn {
  color: var(--v-primary-base, #0066ff) !important;
  border-radius: 6px !important;
}
.tf__reminders ::v-deep .app-form-notify__add .v-btn .v-btn__content {
  color: inherit !important;
}
/* One reminder per line in the narrow side card: type | number | unit | x */
.tf__reminders ::v-deep .app-form-notify__row {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) 52px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.tf__reminders ::v-deep .app-form-notify__field,
.tf__reminders ::v-deep .app-form-notify__field--value {
  flex: none;
  width: auto;
  max-width: none;
  min-width: 0;
}
.tf__reminders ::v-deep .app-form-notify__field .v-input__slot {
  padding: 0 8px !important;
}
.tf__reminders ::v-deep .app-form-notify__field .v-input__append-inner {
  padding-inline-start: 0 !important;
  margin-inline-start: 0 !important;
}
.tf__reminders ::v-deep .app-form-notify__field input {
  min-width: 0 !important;
  font-size: 0.82rem;
}
.tf__reminders ::v-deep .app-form-notify__field--value input {
  text-align: center;
}
.tf__reminders ::v-deep .app-form-notify__field--value .v-input__append-inner {
  display: none;
}
.tf__reminders ::v-deep .app-form-notify__remove {
  width: 28px !important;
  height: 28px !important;
  justify-self: center;
}
/* Trash icon instead of the shared component's ✕ (mdi-trash-can-outline) */
.tf__reminders ::v-deep .app-form-notify__remove .v-icon::before {
  content: "\F0A7A";
}
.tf__reminders ::v-deep .app-form-notify__remove .v-icon {
  font-size: 19px !important;
}
.tf__reminders ::v-deep .app-form-notify__scroll {
  flex: 0 0 auto;
  max-height: 220px;
}
.tf__quick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tf__quick-day {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid #e1e5ec;
  font-size: 0.72rem;
  line-height: 1.4;
  transition: border-color 0.15s, background 0.15s;
}
.tf__quick-day strong {
  font-size: 0.76rem;
}
.tf__quick-day span {
  color: #8a94a6;
}
.tf__quick-day:hover {
  border-color: var(--v-primary-base);
}
.tf__quick-day.on {
  border-color: var(--v-primary-base);
  background: rgba(25, 118, 210, 0.08);
  color: var(--v-primary-base);
}
.tf__pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 110px;
  gap: 8px;
}
.tf__seg {
  display: flex;
  padding: 3px;
  background: #eef1f6;
  border-radius: 8px;
}
.theme--dark .tf__seg {
  background: #2a2a2a;
}
.tf__seg button {
  flex: 1 1 0;
  height: 30px;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #5b6475;
}
.tf__seg button.on {
  background: #fff;
  color: var(--v-primary-base);
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.theme--dark .tf__seg button.on {
  background: #1e1e1e;
}
.tf__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tf__facts span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.74rem;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f1f3f7;
}
.theme--dark .tf__facts span {
  background: #2a2a2a;
}
.tf__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #eef0f4;
}
.theme--dark .tf__foot {
  border-color: #333;
}
.tf__foot-hint {
  font-size: 0.74rem;
  color: #8a94a6;
}

/* mobile: one column, properties under the title, header save button */
.tf--mobile .tf__body {
  flex-direction: column;
  overflow-y: auto;
}
.tf--mobile .tf__main,
.tf--mobile .tf__side {
  overflow: visible;
  width: auto;
  flex: 0 0 auto;
}
.tf--mobile .tf__main {
  padding: 16px 16px 8px;
}
.tf--mobile .tf__side {
  border: 0;
  padding: 8px 12px calc(24px + env(safe-area-inset-bottom));
}
/* narrow desktop/tablet window: stack the panes too */
@media (max-width: 899px) {
  .tf__body {
    flex-direction: column;
    overflow-y: auto;
  }
  .tf__main,
  .tf__side {
    overflow: visible;
    width: auto;
    flex: 0 0 auto;
  }
  .tf__side {
    border-inline-start: 0;
    border-top: 1px solid #eef0f4;
  }
  /* here .tf__body scrolls, not .tf__main */
  .tf__title {
    top: 0;
  }
}
.tf--mobile .tf__title {
  font-size: 1.15rem;
}
.tf--mobile .tf__head {
  padding: 8px 10px;
}
</style>
